/**
 * @author Trendrating <info@trendrating.net>
 *
 * @module api/compute/RankingUi2Api
 * @summary Converts UI ranking paramters to Compute API parameters
 *
 */

import { AppEnvironment } from "../../types/Defaults";
import { Properties } from "../Properties";
export class RankingUi2Api {
  // arbitrary big number: added because the threshold function was
  // not designed and added after
  thresholdValueMax = 99999;

  // fast access to decode the right operator for a
  // tuple (property, function)
  // optionsMap: Record<string, Record<string, any>>;
  fieldsAPI: Properties;

  constructor(environment: AppEnvironment) {
    if (environment == null) {
      throw new Error("[CRITICAL] No environment!");
    }

    this.fieldsAPI = new Properties({ properties: environment["properties"] });
  }

  /**
   *
   * Decode Compute API parameters to UI ranking paramters
   *
   * @param {object[]} rules - ranking rules
   * @param {string} rules[].rankType - one of 'inRange', 'quantile'
   * @param {object} rules[].rankTypeParams - function parameters
   * @param {string} rules[].dimension - one of available security
   *   property (fieldsConfiguration.json)
   * @param {boolean} rules[].rev -  true if descending, false otherwise
   *
   * @returns {array} the rules in UI ranking format
   */
  decode(rules: any) {
    const decoded: any = [];
    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];

      const _rule: {
        function: string;
        functionParams: any;
        operator: string;
        operatorParams: {
          value: any;
        };
        property: string;
      } = {
        function: "",
        functionParams: null,
        operator: "",
        operatorParams: {
          value: rule["rev"] === true ? "desc" : "asc",
        },
        property: rule["dimension"],
      };

      switch (rule.rankType) {
        case "inRange": {
          if (rule["max"] === this.thresholdValueMax) {
            // threshold
            _rule.function = "threshold";
            _rule.functionParams = {
              value: rule["min"],
            };
            _rule["operator"] = "sortByThreshold";
          } else {
            // outlier
            _rule.function = "outlier";
            _rule.functionParams = {
              value: rule["max"],
            };
            _rule["operator"] = "sortByOutlier";
          }

          break;
        }
        case "quantile": {
          _rule.function = "quantile";
          _rule.functionParams = {
            value: rule["rankTypeParams"]["n"],
          };
          _rule["operator"] = "sortByQuantile";
          break;
        }
        default: {
          _rule["function"] = "value";

          const fieldConfiguration = this.fieldsAPI.getRawProperty(
            _rule.property,
            "auto",
          );

          const fmt = fieldConfiguration["formatter"]?.["table"];

          if (fmt != null) {
            if (fmt.type === "number" || fmt.type === "numberBig") {
              if (fmt["options"]["isPercentage"]) {
                _rule["operator"] = "sortByPerformance";
              } else {
                _rule["operator"] = "sortByNumeric";
              }
            } else if (fmt.type === "rating") {
              _rule["operator"] = "sortByRating";
            } else if (fmt.type === "exists") {
              _rule["operator"] = "sortByExists";
            } else {
              _rule["operator"] = "sortByString";
            }
          }
        }
      }

      decoded.push(_rule);
    }

    return decoded;
  }

  /**
   *
   * Encode UI ranking paramters to Compute API parameters
   *
   * @param {object[]} rules - ranking rules
   * @param {string} rules[].function - one of 'threshold', 'outlier',
   *   'quantile'
   * @param {object} rules[].functionParams - function parameters
   * @param {any} rules[].functionParams.value - value of parameter
   * @param {string} rules[].property - one of available security
   *   property (fieldsConfiguration.json)
   * @param {string} rules[].sortBy -  one of 'asc', 'desc'
   *
   * @returns {array} the rules in Compute API format
   */
  encode(rules: any) {
    const encoded: any = [];

    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];

      const _rule: any = {
        dimension: rule["property"],
        rev: rule["operatorParams"]["value"] === "asc" ? false : true,
      };

      switch (rule["property"]) {
        case "f_eps_CC_g_3":
        case "f_sps_CC_g_3": {
          _rule["computation"] = {
            function: "g",
            params: {
              lag: 3,
            },
          };

          break;
        }
        case "f_eps_CC_g_12":
        case "f_sps_CC_g_12": {
          _rule["computation"] = {
            function: "g",
            params: {
              lag: 12,
            },
          };

          break;
        }
        case "ticker": {
          _rule["rankType"] = "string-to-double";

          break;
        }
        default: {
        }
      }

      switch (rule["function"]) {
        case "threshold": {
          _rule["rankType"] = "inRange";
          _rule["min"] = rule["functionParams"]["value"];
          _rule["max"] = this.thresholdValueMax;
          break;
        }
        case "outlier": {
          _rule["rankType"] = "inRange";
          _rule["min"] = -1 * rule["functionParams"]["value"];
          _rule["max"] = rule["functionParams"]["value"];
          break;
        }
        case "quantile": {
          _rule["rankType"] = "quantile";
          _rule["rankTypeParams"] = {
            n: rule["functionParams"]["value"],
            trimOutlier: false,
          };
          break;
        }
      }

      encoded.push(_rule);
    }

    return encoded;
  }
}
